/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import usePostData from "@/hooks/usePostData";
import { defaultThemes } from "@/libs/themesList";
import {
  useCustomTheme,
  useLogoStore,
  usePercentageStore,
} from "@/store/store";
import { User } from "@/types";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { t } from "i18next";
import { Power } from "lucide-react";
import { FC, ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../theme-provider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { LoadingSpinner } from "../ui/loadingSpinner";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "../ui/sidebar";
import { Themes } from "../ui/themes";
import { toast } from "../ui/use-toast";
import HeaderTop from "./HeaderTop";
import ImpersonationBanner from "./ImpersonationBanner";
import Leftbar from "./Leftbar";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";

type ThemeVars = { [key: string]: string };

const getLocalTheme = (): ThemeVars | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem("userTheme");
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

const LayoutDashboard: FC<{ children: ReactNode; showButtons?: boolean }> = ({
  children,
  showButtons,
}) => {
  const isMobile = useIsMobile();
  const { enablePreviewLine, percentage } = usePercentageStore();
  const { isVisible, setIsVisible } = useCustomTheme();
  const { theme, setTheme } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [customColors, setCustomColors] = useState<ThemeVars>(() => {
    const localTheme = getLocalTheme();
    if (localTheme) return localTheme;
    const defaultTheme = defaultThemes.find((t) => t.value === "light");
    return defaultTheme || { name: "", value: "", colors: {} };
  });
  const [open, setOpen] = useState(false);
  const [openBar, setOpenBar] = useState(false);
  const { user } = useAuth();
  const [loadingSaveTheme, setLoadingSaveTheme] = useState(false);
  const { postData } = usePostData<any>();

  const saveCustomThemeToDatabase = async (colors: ThemeVars | string) => {
    try {
      setLoadingSaveTheme(true);
      const res = await postData({
        url: `${import.meta.env.VITE_IP_API}/api/financer/${user?.id}`,
        data: { theme: JSON.stringify(colors) },
      });
      if (res === null) {
        setLoadingSaveTheme(false);
        return;
      }
      localStorage.setItem("userTheme", JSON.stringify(colors));
      toast({ title: "Votre theme a bien été modifié.", duration: 1000 });
      window.location.reload();
      setLoadingSaveTheme(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du thème :", error);
    }
  };

  useEffect(() => {
    if (user?.theme && user?.theme !== "") {
      const parsed = JSON.parse(user.theme);
      const localTheme = getLocalTheme();
      if (JSON.stringify(parsed) !== JSON.stringify(localTheme)) {
        setCustomColors(parsed);
        localStorage.setItem("userTheme", user.theme);
      }
    }
  }, [user]);

  useEffect(() => {
    for (const key in customColors.colors) {
      document.documentElement.style.setProperty(key, customColors.colors[key]);
    }
  }, [customColors]);

  const handleResetTheme = () => {
    const currentThemeData = defaultThemes.find((t) => t.value === "light");
    if (currentThemeData) setCustomColors(currentThemeData);
    saveCustomThemeToDatabase(currentThemeData as ThemeVars);
    setIsEditing(true);
  };

  if (loadingSaveTheme) return "Chargement...";

  return (
    <SidebarProvider>
      <ResizablePanelGroup className="flex h-screen" direction="horizontal">
        <ResizablePanel
          maxSize={openBar ? 30 : 3}
          minSize={openBar ? 16 : 3}
          defaultSize={openBar ? 16 : 3}
        >
          <ScrollArea className="h-screen min-h-0 min-w-0 flex flex-col bg-[#f3f2f2]">
            <AppSidebar user={user} setOpenBar={setOpenBar} />
          </ScrollArea>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel className="flex flex-col min-h-0 min-w-0">
          <ScrollArea className="h-screen w-full overflow-y-auto">
            <main className="flex-1 w-full flex flex-col">
              <ImpersonationBanner />
              {!isMobile && (
                <SidebarTrigger className="fixed bottom-5 z-[49] flex size-8 items-center justify-center rounded-md rounded-s-none border border-s-0 bg-gray-100 text-foreground" />
              )}
              <ScrollArea
                className={`w-full overflow-x-hidden ${
                  showButtons ? "mt-5" : ""
                }`}
              >
                <div className="flex-1 md:pl-10 md:pr-12 md:mx-0">
                  <HeaderTop showButtons={showButtons} />
                </div>
                <div className="mx-5 md:mt-12">{children}</div>
              </ScrollArea>
              {enablePreviewLine && (
                <div
                  className="fixed left-0 top-0 z-[999999] h-1 bg-green-600"
                  style={{ width: percentage + "%" }}
                />
              )}
              {isVisible && (
                <div className="fixed bottom-3 left-1/2 -translate-x-1/2 md:translate-x-0 md:left-0 w-[90%] md:w-full md:h-16 flex justify-center items-center z-50">
                  <Themes
                    customColors={customColors}
                    setCustomColors={setCustomColors}
                    isOpen={open}
                    setIsOpen={setOpen}
                    handleResetTheme={handleResetTheme}
                    saveCustomThemeToDatabase={saveCustomThemeToDatabase}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    setVisible={setIsVisible}
                  />
                </div>
              )}
            </main>
          </ScrollArea>
        </ResizablePanel>
      </ResizablePanelGroup>
    </SidebarProvider>
  );
};

const AppSidebar = ({
  user,
  setOpenBar,
}: {
  user: User | null;
  setOpenBar: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { open, setOpen } = useSidebar();
  const { isAuthenticated, logout, loading } = useAuth();
  const { logoUrl } = useLogoStore();
  const [loadingSaveFinancer, setLoadingSaveFinancer] = useState(false);
  const { postData } = usePostData<any>();
  useEffect(() => {
    setOpenBar(open);
  }, [open]);
  async function handleRemoveLogo() {
    setLoadingSaveFinancer(true);
    const res = await postData({
      url: `${import.meta.env.VITE_IP_API}/api/financer/${user?.id}`,
      data: { logo: "delete" },
    });
    if (res === null) {
      setLoadingSaveFinancer(false);
      return;
    }
    toast({ title: "Votre profil a bien été modifié.", duration: 1000 });
    setLoadingSaveFinancer(false);
  }

  return (
    <div className="h-screen w-full overflow-hidden flex">
      <Sidebar
        collapsible="none"
        className="flex-1 h-full w-full flex flex-col overflow-y-auto"
      >
        <SidebarHeader className="bg-transparent pt-8 pl-4 pr-2">
          <div className="relative flex flex-col items-start gap-2 mb-7 group">
            {open && (
              <div className="bg-muted text-xs px-2 rounded-full border border-[#f4f4f4] dark:border-background">
                {t("Financeur")}
              </div>
            )}
            <Link to="/">
              <div className="max-w-32 relative">
                {!loading &&
                  (logoUrl ? (
                    <img
                      src={import.meta.env.VITE_MEDIA_BASE + logoUrl}
                      alt="logo verdeo"
                      className="w-full h-auto"
                    />
                  ) : (
                    <img
                      src="/logo.svg"
                      alt="logo verdeo"
                      className="w-24 h-auto"
                    />
                  ))}
              </div>
            </Link>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <Leftbar handleToggleLeftbar={setOpen} isOpen={open} />
        </SidebarContent>
        <SidebarFooter className="pb-8 pl-4">
          <div
            className={`mt-auto flex flex-wrap ${
              open ? "justify-between" : "justify-center"
            } items-center transition-all`}
          >
            {!isAuthenticated && !loading ? (
              <LoadingSpinner />
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <div className="flex cursor-pointer items-center gap-2 text-muted-foreground transition-all hover:text-destructive">
                    <Power size={18} className="text-muted-foreground" />
                    {open && t("Déconnexion")}
                  </div>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t("Êtes-vous sûr de vouloir vous déconnecter ?")}
                    </AlertDialogTitle>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("Annuler")}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => logout(window.location.href)}
                    >
                      {t("Se déconnecter")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </SidebarFooter>
      </Sidebar>
    </div>
  );
};

export default LayoutDashboard;
